import { formatPrice } from "../../../utils/utils";
import { COUNTRY_CONFIG, DEFAULT_COUNTRY } from "../../../utils/constants";

export const ITEMS_PER_PAGE = 4;
const BASIC_2_PACKAGE = "Basic 2";

export const VALIDATION_MESSAGES = {
  firstName: "Ime je obavezno",
  lastName: "Prezime je obavezno",
  email: "Email je obavezan",
  emailInvalid: "Email adresa nije validna",
  phone: "Telefon je obavezan",
  address: "Adresa je obavezna",
  city: "Grad je obavezan",
  zipCode: "Poštanski broj je obavezan",
};

export const FORM_DATA_KEY = "cartFormData";

export const getSellerInfo = (item) => {
  if (item.band?.id) {
    return {
      sellerType: "band",
      sellerId: item.band.id,
      sellerName: item.band.name,
      subscriptionPackage:
        item.band.adminUsers?.[0]?.subscriptionPackage || null,
    };
  }
  if (item.festival?.id) {
    return {
      sellerType: "festival",
      sellerId: item.festival.id,
      sellerName: item.festival.name,
      subscriptionPackage:
        item.festival.adminUsers?.[0]?.subscriptionPackage || null,
    };
  }
  return null;
};

const isBasic2Seller = (item) => {
  const seller = getSellerInfo(item);
  return seller?.subscriptionPackage === BASIC_2_PACKAGE;
};

export const makeSellerKey = (seller) =>
  `${seller.sellerType}:${seller.sellerId}`;

const createSellerGroup = (seller) => ({
  sellerType: seller.sellerType,
  sellerId: seller.sellerId,
  sellerName: seller.sellerName,
  bandName: seller.sellerName,
  bandId: seller.sellerType === "band" ? seller.sellerId : null,
  festivalId: seller.sellerType === "festival" ? seller.sellerId : null,
  items: [],
});

export const groupItemsByShipping = (items = []) => {
  const groups = {
    centralShipping: [],
    bandShipping: new Map(),
  };

  items.forEach((item) => {
    const seller = getSellerInfo(item);
    if (seller && isBasic2Seller(item)) {
      const sellerKey = makeSellerKey(seller);
      if (!groups.bandShipping.has(sellerKey)) {
        groups.bandShipping.set(sellerKey, createSellerGroup(seller));
      }
      groups.bandShipping.get(sellerKey).items.push(item);
    } else {
      groups.centralShipping.push(item);
    }
  });

  return groups;
};

export const getCountryConfig = (countryCode) =>
  COUNTRY_CONFIG[countryCode] || COUNTRY_CONFIG[DEFAULT_COUNTRY];

export const getShippingCostForCountry = (countryCode) =>
  getCountryConfig(countryCode).deliveryPrice;

export const formatShipping = (rsdAmount, countryCode) => {
  if (rsdAmount === 0) return "Besplatno";
  const cfg = getCountryConfig(countryCode);
  if (cfg.deliveryPriceEur) {
    return `${cfg.deliveryPriceEur} € (≈ ${formatPrice(rsdAmount)})`;
  }
  return `≈ ${formatPrice(rsdAmount)}`;
};

export const computeTotals = ({
  itemsByShippingGroup,
  deliveryMethod,
  country,
}) => {
  const cfg = getCountryConfig(country);
  const shippingCost = cfg.deliveryPrice;
  const isPickup = deliveryMethod === "pickup";

  const computeShipmentShipping = (subtotal) => {
    if (isPickup) return 0;
    if (cfg.freeShippingThreshold && subtotal >= cfg.freeShippingThreshold) {
      return 0;
    }
    return shippingCost;
  };

  const centralShippingSubtotal = itemsByShippingGroup.centralShipping.reduce(
    (total, item) => total + (item.salePrice || item.price) * item.quantity,
    0,
  );

  const bandGroups = [];
  let totalBandShippingSubtotal = 0;
  let totalBandShippingCost = 0;

  itemsByShippingGroup.bandShipping.forEach((bandGroup, sellerKey) => {
    const bandSubtotal = bandGroup.items.reduce(
      (total, item) => total + (item.salePrice || item.price) * item.quantity,
      0,
    );
    totalBandShippingSubtotal += bandSubtotal;

    const groupShipping = computeShipmentShipping(bandSubtotal);
    totalBandShippingCost += groupShipping;

    bandGroups.push({
      sellerKey,
      sellerType: bandGroup.sellerType,
      sellerId: bandGroup.sellerId,
      sellerName: bandGroup.sellerName,
      bandId: bandGroup.bandId,
      festivalId: bandGroup.festivalId,
      bandName: bandGroup.bandName,
      subtotal: bandSubtotal,
      shippingCost: groupShipping,
      itemCount: bandGroup.items.length,
    });
  });

  const subtotal = totalBandShippingSubtotal + centralShippingSubtotal;
  const centralShippingCost =
    itemsByShippingGroup.centralShipping.length > 0
      ? computeShipmentShipping(centralShippingSubtotal)
      : 0;
  const shipping = centralShippingCost + totalBandShippingCost;
  const total = subtotal + shipping;

  const totalShipments =
    (itemsByShippingGroup.centralShipping.length > 0 ? 1 : 0) +
    bandGroups.length;

  return {
    subtotal,
    shipping,
    total,
    centralShippingSubtotal,
    centralShippingCost,
    bandGroups,
    totalBandShippingCost,
    hasMultipleShipments: totalShipments > 1,
    totalShipments,
  };
};

export const validateCheckoutForm = (formData, deliveryMethod) => {
  const newErrors = {};
  const emailRegex = /\S+@\S+\.\S+/;

  const basicValidationRules = [
    { field: "firstName", message: VALIDATION_MESSAGES.firstName },
    { field: "lastName", message: VALIDATION_MESSAGES.lastName },
    { field: "email", message: VALIDATION_MESSAGES.email },
    { field: "phone", message: VALIDATION_MESSAGES.phone },
  ];

  basicValidationRules.forEach(({ field, message }) => {
    if (!formData[field].trim()) {
      newErrors[field] = message;
    }
  });

  if (deliveryMethod === "delivery") {
    [
      { field: "address", message: VALIDATION_MESSAGES.address },
      { field: "city", message: VALIDATION_MESSAGES.city },
      { field: "zipCode", message: VALIDATION_MESSAGES.zipCode },
    ].forEach(({ field, message }) => {
      if (!formData[field].trim()) {
        newErrors[field] = message;
      }
    });
  }

  if (formData.email.trim() && !emailRegex.test(formData.email)) {
    newErrors.email = VALIDATION_MESSAGES.emailInvalid;
  }

  return newErrors;
};

export const createBaseOrderData = (formData, deliveryMethod, country) => ({
  firstName: formData.firstName,
  lastName: formData.lastName,
  email: formData.email,
  phone: formData.phone,
  address: deliveryMethod === "pickup" ? null : formData.address,
  city: deliveryMethod === "pickup" ? null : formData.city,
  zipCode: deliveryMethod === "pickup" ? null : formData.zipCode,
  country,
  details: formData.details,
  deliveryMethod,
});

export const createProductsArray = (items) =>
  items.map((item) => ({
    id: item.id,
    quantity: item.quantity,
    ...(item.selectedSize && { selectedSize: item.selectedSize }),
  }));

export const createOrderLabel = (type, sellerName = null) => {
  if (type === "central") return "[CENTRALNA DOSTAVA]";
  if (type === "festival") return `[DOSTAVA OD FESTIVALA: ${sellerName}]`;
  return `[DOSTAVA OD BENDA: ${sellerName}]`;
};

export const saveFormData = (formData) => {
  const hasData = Object.values(formData).some((value) => value?.trim());
  if (hasData) {
    localStorage.setItem(FORM_DATA_KEY, JSON.stringify(formData));
  }
};

export const loadFormData = () => {
  try {
    const saved = localStorage.getItem(FORM_DATA_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    return null;
  }
};

export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};
